import type { WhaleChannel, WhaleRow } from '@/api/types';

// CRM's reward bands: what the player cost us in reward, cashback and converted bonus as a share of
// the GGR they produced. A player with no positive GGR has no share; they are a loss.
export type WhaleBand = 'under' | 'mid' | 'high' | 'loss';
export type WhaleFilter = 'all' | WhaleBand | 'restricted';

export const WHALE_FILTERS: { value: WhaleFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'under', label: 'Reward share under 5%' },
  { value: 'mid', label: 'Reward share 5–15%' },
  { value: 'high', label: 'Reward share 15% or more' },
  { value: 'loss', label: 'Loss (GGR zero or below)' },
  { value: 'restricted', label: 'Suspended or blocked' },
];

const RESTRICTED_STATUSES = ['SUSPENDED', 'BLOCKED'];

export const rewardCostEur = (row: WhaleRow): number => row.rewardEur + row.cashbackEur + row.bonusConvertedEur;

export function rewardShare(row: WhaleRow): number | null {
  return row.ggrEur > 0 ? rewardCostEur(row) / row.ggrEur : null;
}

export function bandOf(row: WhaleRow): WhaleBand {
  const share = rewardShare(row);
  if (share === null) return 'loss';
  if (share < 0.05) return 'under';
  if (share < 0.15) return 'mid';
  return 'high';
}

export interface WhaleTableFilters {
  band: WhaleFilter;
  // '' is every country.
  country: string;
}

export const NO_WHALE_FILTERS: WhaleTableFilters = { band: 'all', country: '' };

function matchesBand(row: WhaleRow, band: WhaleFilter): boolean {
  if (band === 'all') return true;
  if (band === 'restricted') return RESTRICTED_STATUSES.includes(row.profile?.status ?? '');
  return bandOf(row) === band;
}

export function filterWhales(rows: readonly WhaleRow[], filters: WhaleTableFilters): WhaleRow[] {
  return rows.filter(
    (row) => matchesBand(row, filters.band) && (filters.country === '' || row.country === filters.country),
  );
}

// Most represented first, so the countries worth filtering by lead the list.
export function countryOptions(rows: readonly WhaleRow[]): { country: string; players: number }[] {
  const counts = new Map<string, number>();
  rows.forEach((row) => {
    if (row.country) counts.set(row.country, (counts.get(row.country) ?? 0) + 1);
  });
  return [...counts]
    .map(([country, players]) => ({ country, players }))
    .sort((a, b) => b.players - a.players || a.country.localeCompare(b.country));
}

export interface WhaleTotals {
  players: number;
  // Players the house lost to.
  winners: number;
  depositCount: number;
  depositEur: number;
  ggrEur: number;
  ngrEur: number;
  rewardCostEur: number;
  // GGR kept per euro deposited; null without deposits.
  holdRate: number | null;
  // Reward cost against the GGR of the players who produced any, as CRM measures it.
  rewardShare: number | null;
}

export function totalsOf(rows: readonly WhaleRow[]): WhaleTotals {
  const sum = (value: (row: WhaleRow) => number) => rows.reduce((total, row) => total + value(row), 0);
  const depositEur = sum((row) => row.depositEur);
  const ggrEur = sum((row) => row.ggrEur);
  const cost = sum(rewardCostEur);
  const positiveGgr = sum((row) => Math.max(row.ggrEur, 0));
  return {
    players: rows.length,
    winners: rows.filter((row) => row.ggrEur < 0).length,
    depositCount: sum((row) => row.depositCount),
    depositEur,
    ggrEur,
    ngrEur: sum((row) => row.ngrEur),
    rewardCostEur: cost,
    holdRate: depositEur > 0 ? ggrEur / depositEur : null,
    rewardShare: positiveGgr > 0 ? cost / positiveGgr : null,
  };
}

// veli-edge's channel names as support says them.
const CHANNEL_LABELS: Record<string, string> = {
  email: 'Email',
  sms: 'SMS',
  web_push: 'Push',
  web_popup: 'Popup',
  message_feed: 'Feed',
  include_campaign: 'Segment',
  give_reward: 'Reward',
};

export const channelLabel = (channel: string): string => CHANNEL_LABELS[channel] ?? channel;

export interface PreferredChannel extends WhaleChannel {
  // Of the player's messages followed by a deposit within 24 hours, the share sent through this channel.
  share: number;
}

// What worked historically: each channel's share of the messages a deposit followed within 24 hours,
// most first. Empty when no message was followed by one.
export function preferredChannels(channels: readonly WhaleChannel[]): PreferredChannel[] {
  const deposited = channels.reduce((total, channel) => total + channel.deposited24h, 0);
  if (deposited === 0) return [];
  return channels
    .filter((channel) => channel.deposited24h > 0)
    .map((channel) => ({ ...channel, share: channel.deposited24h / deposited }))
    .sort((a, b) => b.share - a.share || a.channel.localeCompare(b.channel));
}
